<div class="df-page">
  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="df-archived" />
  }

  @if (state() === 'notFound') {
    <div class="df-card df-state">
      <p class="df-state__text" role="alert" data-testid="defect-not-found">
        {{ 'defect.detail.notFound' | translate }}
      </p>
      <a
        class="df-btn-secondary df-state__action"
        data-testid="defect-not-found-back"
        [routerLink]="['/projects', projectId, 'defects']"
      >
        {{ 'defect.detail.backToList' | translate }}
      </a>
    </div>
  } @else {
    <div class="df-toolbar">
      <a class="df-back" data-testid="defect-back" [routerLink]="['/projects', projectId, 'defects']">
        <mat-icon aria-hidden="true">chevron_left</mat-icon>
        {{ 'defect.detail.backToList' | translate }}
      </a>
    </div>

    @if (defect(); as d) {
      <header class="df-head" data-testid="defect-head">
        <code class="df-key" data-testid="defect-key">{{ d.key }}</code>
        <div class="df-head__main">
          <h1 class="df-head__title" data-testid="defect-title">{{ d.title }}</h1>
          @if (draft(); as draft) {
            <div class="df-head__pills">
              <span
                class="df-pill"
                data-testid="defect-head-severity"
                [class]="'df-pill--' + severityTone(draft.severity)"
              >
                <span class="df-pill__dot" aria-hidden="true"></span>
                {{ 'defect.severity.' + draft.severity | translate }}
              </span>
              <span class="df-pill" data-testid="defect-head-status" [class]="'df-pill--' + statusTone(draft.status)">
                <span class="df-pill__dot" aria-hidden="true"></span>
                {{ 'defect.status.' + draft.status | translate }}
              </span>
              @if (d.markedBlocking) {
                <span
                  class="df-blocking-badge"
                  data-testid="defect-head-blocking"
                  [title]="'defect.detail.blockingTooltip' | translate"
                >
                  {{ 'defect.list.blocking' | translate }}
                </span>
              }
              @if (retestOpen()) {
                <span class="df-retest-pill" data-testid="defect-head-retest-open">
                  @if (d.retest?.retestBy?.name; as name) {
                    {{ 'defect.detail.retest.openPill' | translate: { name } }}
                  } @else {
                    {{ 'defect.detail.retest.open' | translate }}
                  }
                </span>
              }
            </div>
          }
        </div>
      </header>
    }

    @if (versionConflict()) {
      <div class="df-message" role="alert" data-testid="defect-conflict">
        <span class="df-message__text">{{ 'defect.detail.error.versionConflict' | translate }}</span>
        <button type="button" class="df-btn-secondary" data-testid="defect-reload" (click)="load()">
          <span aria-hidden="true">↻</span>
          {{ 'defect.detail.reload' | translate }}
        </button>
      </div>
    } @else if (errorText(); as message) {
      <div class="df-message" role="alert" data-testid="defect-update-error">
        <span class="df-message__text">{{ message }}</span>
      </div>
    }
    @if (saved() && !errorText() && !versionConflict()) {
      <app-success-banner class="df-saved" messageKey="defect.detail.saved">
        @if (retestNotice(); as notice) {
          <span class="df-saved__notice" data-testid="defect-retest-notice">
            {{ 'defect.detail.retest.notified' | translate: notice }}
          </span>
        }
      </app-success-banner>
    }

    @switch (state()) {
      @case ('loading') {
        <div class="df-card df-state" data-testid="defect-loading">
          <mat-spinner diameter="32" />
        </div>
      }
      @case ('error') {
        <div class="df-card df-state" role="alert" data-testid="defect-load-error">
          <p class="df-state__text">{{ 'defect.detail.loadError' | translate }}</p>
          <button type="button" class="df-btn-secondary df-state__action" data-testid="defect-retry" (click)="load()">
            {{ 'defect.detail.retry' | translate }}
          </button>
        </div>
      }
      @case ('ok') {
        @if (defect(); as d) {
          <div class="df-detail" data-testid="defect-grid">
            <div class="df-main">
              <section class="df-card df-section" aria-labelledby="df-description-title">
                <div class="df-section__head">
                  <h2 id="df-description-title" class="df-section__title">
                    {{ 'defect.detail.description' | translate }}
                  </h2>
                </div>
                @if (d.description) {
                  <p class="df-description" data-testid="defect-description">{{ d.description }}</p>
                } @else {
                  <p class="df-description df-missing" data-testid="defect-description">–</p>
                }
              </section>

              <section class="df-card df-section" data-testid="defect-found-in" aria-labelledby="df-found-title">
                <div class="df-section__head">
                  <h2 id="df-found-title" class="df-section__title">{{ 'defect.detail.foundIn' | translate }}</h2>
                  <p class="df-section__subtitle">{{ 'defect.detail.foundInSubtitle' | translate }}</p>
                </div>
                <dl class="df-facts">
                  <div>
                    <dt>{{ 'defect.detail.testRun' | translate }}</dt>
                    <dd data-testid="defect-found-run">
                      @if (d.foundIn?.testRunKey; as runKey) {
                        @if (d.foundIn?.testRunId; as runId) {
                          <a
                            class="df-chip"
                            data-testid="defect-chip-run"
                            [routerLink]="['/projects', projectId, 'test-runs', runId, 'executions']"
                          >
                            {{ runKey }}
                            <span class="df-chip__arrow" aria-hidden="true">↗</span>
                          </a>
                        } @else {
                          <span class="df-chip df-chip--static" data-testid="defect-chip-run">{{ runKey }}</span>
                        }
                      } @else {
                        <span class="df-missing" data-testid="defect-missing">–</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>{{ 'defect.detail.testCase' | translate }}</dt>
                    <dd data-testid="defect-found-test-case">
                      @if (d.foundIn?.testCaseKey; as testCaseKey) {
                        @if (d.foundIn?.testCaseId; as testCaseId) {
                          <a
                            class="df-chip"
                            data-testid="defect-chip-test-case"
                            [routerLink]="['/projects', projectId, 'test-cases', testCaseId, 'edit']"
                          >
                            {{ testCaseKey }}
                            <span class="df-chip__arrow" aria-hidden="true">↗</span>
                          </a>
                        } @else {
                          <span class="df-chip df-chip--static" data-testid="defect-chip-test-case">
                            {{ testCaseKey }}
                          </span>
                        }
                      } @else {
                        <span class="df-missing" data-testid="defect-missing">–</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>{{ 'defect.detail.step' | translate }}</dt>
                    <dd data-testid="defect-found-step">
                      @if (d.foundIn?.stepNumber; as stepNumber) {
                        <span class="df-step">{{ 'defect.detail.stepLabel' | translate: { n: stepNumber } }}</span>
                      } @else {
                        <span class="df-missing" data-testid="defect-missing">–</span>
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>{{ 'defect.detail.release' | translate }}</dt>
                    <dd data-testid="defect-found-release">
                      @if (d.foundIn?.releaseName; as releaseName) {
                        <a
                          class="df-chip"
                          data-testid="defect-chip-release"
                          [routerLink]="['/projects', projectId, 'test-results']"
                        >
                          {{ releaseName }}
                          <span class="df-chip__arrow" aria-hidden="true">↗</span>
                        </a>
                      } @else {
                        <span class="df-missing" data-testid="defect-missing">–</span>
                      }
                    </dd>
                  </div>
                </dl>
                <div class="df-retest" data-testid="defect-verified-in">
                  <span class="df-label" data-testid="defect-verified-in-label">
                    {{ (retestOpen() ? 'defect.detail.retest.title' : 'defect.detail.verifiedIn') | translate }}
                  </span>
                  @if (retestOpen()) {
                    <div class="df-retest-open" data-testid="defect-retest-open">
                      <div class="df-retest-open__row">
                        @if (d.retest?.retestBy?.name; as name) {
                          <app-avatar [name]="name" [size]="30" />
                        }
                        <div class="df-retest-open__text">
                          <div class="df-retest-open__who" data-testid="defect-retest-open-who">
                            @if (d.retest?.retestBy?.name; as name) {
                              {{ 'defect.detail.retest.openAt' | translate: { name } }}
                            } @else {
                              {{ 'defect.detail.retest.open' | translate }}
                            }
                          </div>
                          @if (retestMeta(d); as meta) {
                            <div class="df-retest-open__meta" data-testid="defect-retest-open-meta">{{ meta }}</div>
                          }
                        </div>
                        @if (canStartRetest()) {
                          <button
                            type="button"
                            class="df-btn-primary"
                            data-testid="defect-start-retest"
                            [disabled]="startingRetest()"
                            (click)="startRetest()"
                          >
                            <span aria-hidden="true">▶</span>
                            {{ 'defect.detail.retest.start' | translate }}
                          </button>
                        }
                      </div>
                      <p class="df-retest-open__outcome" data-testid="defect-retest-outcome">
                        {{
                          'defect.detail.retest.outcome'
                            | translate
                              : { person: d.assignee?.name ?? ('defect.detail.retest.assigneeFallback' | translate) }
                        }}
                      </p>
                    </div>
                  } @else if (retest(); as passed) {
                    <div class="df-retest__row">
                      @if (passed.testRunId) {
                        <a
                          class="df-chip"
                          data-testid="defect-chip-retest"
                          [routerLink]="['/projects', projectId, 'test-runs', passed.testRunId, 'executions']"
                        >
                          {{ passed.testRunKey ?? '–' }}
                          <span class="df-chip__arrow" aria-hidden="true">↗</span>
                        </a>
                      } @else {
                        <span class="df-chip df-chip--static" data-testid="defect-chip-retest">
                          {{ passed.testRunKey ?? '–' }}
                        </span>
                      }
                      <span class="df-pill df-pill--green" data-testid="defect-retest-passed">
                        <span class="df-pill__dot" aria-hidden="true"></span>
                        {{ 'defect.detail.retestPassed' | translate }}
                      </span>
                    </div>
                  } @else {
                    <span class="df-retest__none" data-testid="defect-no-retest">
                      {{ 'defect.detail.noRetest' | translate }}
                    </span>
                  }
                </div>
              </section>

              <section class="df-card df-section" aria-labelledby="df-evidence-title">
                <div class="df-section__head">
                  <h2 id="df-evidence-title" class="df-section__title">{{ 'defect.detail.evidence' | translate }}</h2>
                  @if (d.evidence.length > 0) {
                    <p class="df-section__subtitle" data-testid="defect-evidence-count">
                      {{
                        (d.evidence.length === 1 ? 'defect.detail.evidenceCountOne' : 'defect.detail.evidenceCountMany')
                          | translate: { count: d.evidence.length }
                      }}
                    </p>
                  }
                </div>
                @if (d.evidence.length === 0) {
                  <p class="df-evidence__empty" data-testid="defect-evidence-empty">
                    {{ 'defect.detail.noEvidence' | translate }}
                  </p>
                } @else {
                  <ul class="df-evidence">
                    @for (file of d.evidence; track file.attachmentId) {
                      <li class="df-evidence__row" data-testid="defect-evidence">
                        <div
                          class="df-evidence__thumb"
                          [class.df-evidence__thumb--image]="isImage(file)"
                          data-testid="defect-evidence-thumb"
                        >
                          @if (previews()[file.attachmentId]; as url) {
                            <img [src]="url" alt="" data-testid="defect-evidence-preview" />
                          } @else {
                            <mat-icon aria-hidden="true" data-testid="defect-evidence-icon">
                              {{ isImage(file) ? 'image' : 'table_chart' }}
                            </mat-icon>
                          }
                        </div>
                        <div class="df-evidence__text">
                          <span class="df-evidence__name" data-testid="defect-evidence-name">
                            {{ file.originalFilename }}
                          </span>
                          <span class="df-evidence__size" data-testid="defect-evidence-size">{{ fileSize(file) }}</span>
                        </div>
                        <button
                          type="button"
                          class="df-evidence__download"
                          data-testid="defect-evidence-download"
                          [attr.aria-label]="
                            'defect.detail.downloadEvidence' | translate: { name: file.originalFilename }
                          "
                          [title]="'defect.detail.download' | translate"
                          (click)="download(file)"
                        >
                          <span aria-hidden="true">↓</span>
                        </button>
                      </li>
                    }
                  </ul>
                }
              </section>
            </div>

            <aside class="df-side" data-testid="defect-side">
              <section
                class="df-card df-status-card"
                data-testid="defect-status-card"
                aria-labelledby="df-status-title"
              >
                <h2 id="df-status-title" class="df-status-card__title">{{ 'defect.detail.overview' | translate }}</h2>
                @if (draft(); as draft) {
                  <div class="df-card-fields">
                    @if (isReadOnly()) {
                      <div>
                        <span class="df-label">{{ 'defect.detail.status' | translate }}</span>
                        <div data-testid="defect-readonly-status">
                          <span class="df-pill" [class]="'df-pill--' + statusTone(d.status)">
                            <span class="df-pill__dot" aria-hidden="true"></span>
                            {{ 'defect.status.' + d.status | translate }}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span class="df-label">{{ 'defect.detail.assignee' | translate }}</span>
                        <div class="df-readonly-value" data-testid="defect-readonly-assignee">
                          {{ d.assignee?.name ?? ('defect.list.unassigned' | translate) }}
                        </div>
                      </div>
                      <div>
                        <span class="df-label">{{ 'defect.detail.severity' | translate }}</span>
                        <div data-testid="defect-readonly-severity">
                          <span class="df-pill" [class]="'df-pill--' + severityTone(d.severity)">
                            <span class="df-pill__dot" aria-hidden="true"></span>
                            {{ 'defect.severity.' + d.severity | translate }}
                          </span>
                        </div>
                      </div>
                    } @else {
                      <div>
                        <label class="df-label" for="df-status">{{ 'defect.detail.status' | translate }}</label>
                        <select
                          #statusEl
                          id="df-status"
                          class="df-select"
                          data-testid="defect-status"
                          [attr.aria-label]="'defect.detail.status' | translate"
                          [disabled]="saving()"
                          (change)="changeStatus(statusEl)"
                        >
                          @for (option of statusOptions(); track option.status) {
                            <option
                              [value]="option.status"
                              [selected]="option.status === draft.status"
                              [disabled]="!option.allowed"
                              [attr.title]="
                                option.reason === 'needsRetest'
                                  ? ('defect.detail.retestRequired' | translate)
                                  : option.reason === 'notAllowed'
                                    ? ('defect.detail.transitionNotAllowed' | translate)
                                    : null
                              "
                            >
                              {{ 'defect.status.' + option.status | translate
                              }}{{ option.reason === 'needsRetest' ? ('defect.detail.retestSuffix' | translate) : '' }}
                            </option>
                          }
                        </select>
                        <p class="df-flow-hint">{{ 'defect.detail.flowHint' | translate }}</p>
                      </div>
                      @if (draft.status === 'FIXED') {
                        <div class="df-fixed-block" data-testid="defect-fixed-block">
                          <div>
                            <label class="df-label" for="df-fixed-in-build">
                              {{ 'defect.detail.retest.fixedInBuild' | translate }}
                              <span class="df-required" aria-hidden="true">*</span>
                            </label>
                            <input
                              #buildEl
                              id="df-fixed-in-build"
                              class="df-input"
                              data-testid="defect-fixed-in-build"
                              required
                              maxlength="100"
                              [class.df-input--invalid]="buildInvalid()"
                              [value]="draft.fixedInBuild"
                              [placeholder]="'defect.detail.retest.buildPlaceholder' | translate"
                              [disabled]="saving()"
                              [attr.aria-invalid]="buildInvalid()"
                              aria-describedby="df-build-hint"
                              (input)="changeFixedInBuild(buildEl)"
                              (blur)="touchBuild()"
                            />
                            <p
                              id="df-build-hint"
                              class="df-build-hint"
                              data-testid="defect-build-hint"
                              [class.df-build-hint--error]="buildInvalid()"
                            >
                              {{
                                (buildInvalid()
                                  ? 'defect.detail.retest.buildRequired'
                                  : 'defect.detail.retest.buildHint'
                                ) | translate
                              }}
                            </p>
                          </div>
                          <app-person-select
                            class="df-field"
                            data-testid="defect-retest-by"
                            [formControl]="retestByControl"
                            [label]="'defect.detail.retest.retestBy' | translate"
                            [options]="personOptions()"
                            [allowNone]="false"
                          />
                          @if (d.status !== 'FIXED') {
                            <label class="df-notify">
                              <input
                                #notifyEl
                                type="checkbox"
                                data-testid="defect-request-retest"
                                [checked]="draft.requestRetest"
                                [disabled]="saving()"
                                (change)="changeRequestRetest(notifyEl.checked)"
                              />
                              <span data-testid="defect-request-retest-label">
                                {{ 'defect.detail.retest.notify' | translate }}
                              </span>
                            </label>
                          }
                        </div>
                      }
                      <app-person-select
                        class="df-field"
                        data-testid="defect-assignee"
                        [formControl]="assigneeControl"
                        [label]="'defect.detail.assignee' | translate"
                        [options]="personOptions()"
                        [allowNone]="true"
                        noneKey="defect.list.unassigned"
                      />
                      <div>
                        <label class="df-label" for="df-severity">{{ 'defect.detail.severity' | translate }}</label>
                        <select
                          #severityEl
                          id="df-severity"
                          class="df-select"
                          data-testid="defect-severity"
                          [attr.aria-label]="'defect.detail.severity' | translate"
                          [disabled]="saving()"
                          (change)="changeSeverity(severityEl)"
                        >
                          @for (severity of severities; track severity) {
                            <option [value]="severity" [selected]="severity === draft.severity">
                              {{ 'defect.severity.' + severity | translate }}
                            </option>
                          }
                        </select>
                      </div>
                    }
                    <mat-checkbox
                      class="df-marked-blocking"
                      data-testid="defect-marked-blocking"
                      [checked]="draft.markedBlocking"
                      [disabled]="isReadOnly() || saving()"
                      (change)="changeMarkedBlocking($event.checked)"
                    >
                      {{ 'defect.list.blocking' | translate }}
                    </mat-checkbox>
                    <div class="df-created" data-testid="defect-created-by">
                      <span class="df-label">{{ 'defect.detail.createdBy' | translate }}</span>
                      <div class="df-created__person">
                        @if (d.createdBy?.name; as name) {
                          <app-avatar [name]="name" [size]="30" />
                        }
                        <div>
                          <div class="df-created__name">{{ d.createdBy?.name ?? '–' }}</div>
                          <div class="df-created__date">{{ d.createdAt | date: 'dd.MM.yyyy' }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  @if (!isReadOnly()) {
                    <div class="df-actions">
                      <button
                        type="button"
                        class="df-btn-secondary"
                        data-testid="defect-discard"
                        [disabled]="saving()"
                        (click)="discard()"
                      >
                        {{ 'defect.detail.discard' | translate }}
                      </button>
                      <button
                        type="button"
                        class="df-save"
                        data-testid="defect-save"
                        [class.df-save--active]="dirty() && !saving()"
                        [disabled]="!dirty() || saving()"
                        (click)="save()"
                      >
                        {{ 'defect.detail.save' | translate }}
                      </button>
                    </div>
                  }
                }
              </section>
            </aside>
          </div>
        }
      }
    }
  }

  <lib-modal
    [closeLabel]="'general.close' | translate"
    [title]="'common.unsavedChanges.title' | translate"
    [text]="'defect.detail.unsavedMessage' | translate"
    [visible]="showDeactivateConfirm()"
    [saveText]="'common.unsavedChanges.leave' | translate"
    [cancelText]="'common.unsavedChanges.stay' | translate"
    (save)="onDeactivateConfirmed()"
    (cancel)="onDeactivateCancelled()"
    (visibleChange)="!$event && onDeactivateCancelled()"
  ></lib-modal>
</div>
